/**
 * AUTO-6 — Sysadmin Builder Analytics TanStack Query hooks.
 *
 * Backs `/sysadmin/platform/builder-analytics`. Schemas live HERE so
 * the wire types stay in lockstep with the Fastify route schemas in
 * `packages/api/src/routes/v1/admin/platform-config/builder-analytics.ts`.
 *
 * Conventions follow `use-sysadmin-ep1.ts` (same author, same patterns):
 *   - Hooks use `createSysadminQuery` with a per-range cache key so
 *     date-range changes do not collide.
 *   - 10-second refresh interval inherited from
 *     SYSADMIN_DEFAULT_REFRESH_MS — matches every other sysadmin
 *     operational surface so the whole console ticks together.
 */

import { z } from 'zod';
import { createSysadminQuery } from './use-sysadmin-query';

// ─── Shared enums ────────────────────────────────────────────────────────

export const BUILDER_KIND_ENUM = [
  'tool_helper',
  'skill_helper',
  'prompt_helper',
  'function_helper',
  'architect',
  'ask_argus',
] as const;

export const BuilderKindSchema = z.enum(BUILDER_KIND_ENUM);
export type BuilderKind = z.infer<typeof BuilderKindSchema>;

export const BUILDER_KIND_LABELS: Record<BuilderKind, string> = {
  tool_helper: 'Tool Helper',
  skill_helper: 'Skill Helper',
  prompt_helper: 'Prompt Helper',
  function_helper: 'Function Helper',
  architect: 'Agent Architect',
  ask_argus: 'Ask Argus',
};

// ─── Summary ─────────────────────────────────────────────────────────────

export const BuilderSummaryCardSchema = z.object({
  builderKind: BuilderKindSchema,
  turnCount: z.number().int(),
  successCount: z.number().int(),
  successRate: z.number(),
  timeoutFirstTokenCount: z.number().int(),
  timeoutStreamInactivityCount: z.number().int(),
  firstTokenP50Ms: z.number().int().nullable(),
  firstTokenP95Ms: z.number().int().nullable(),
  totalP50Ms: z.number().int().nullable(),
  totalP95Ms: z.number().int().nullable(),
  toolCallAttemptCount: z.number().int(),
  toolCallSuccessCount: z.number().int(),
  toolCallSuccessRate: z.number(),
  discoveryTurnCount: z.number().int(),
  stubDraftCount: z.number().int(),
  discoveryContractPassRate: z.number(),
  mutationCount: z.number().int(),
});
export type BuilderSummaryCard = z.infer<typeof BuilderSummaryCardSchema>;

export const BuilderSummaryResponseSchema = z.object({
  range: z.object({ from: z.string(), to: z.string() }),
  builders: z.array(BuilderSummaryCardSchema),
});
export type BuilderSummaryResponse = z.infer<
  typeof BuilderSummaryResponseSchema
>;

export interface DateRange {
  /** YYYY-MM-DD or undefined to fall back to server default (last 7d). */
  from?: string;
  to?: string;
}

function buildQs(range: DateRange): string {
  const sp = new URLSearchParams();
  if (range.from) sp.set('from', range.from);
  if (range.to) sp.set('to', range.to);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Summary cards: one per builder kind. Range optional — defaults to
 * the last 7 days server-side.
 */
export const useBuilderAnalyticsSummary = (range: DateRange = {}) =>
  createSysadminQuery({
    key: [
      'sysadmin-builder-analytics-summary',
      range.from ?? 'default',
      range.to ?? 'default',
    ],
    url: `/admin/platform/builder-analytics${buildQs(range)}`,
    schema: BuilderSummaryResponseSchema,
  })();

// ─── Top tools ───────────────────────────────────────────────────────────

export const BuilderTopToolsRowSchema = z.object({
  toolSlug: z.string(),
  attemptCount: z.number().int(),
  successCount: z.number().int(),
  failCount: z.number().int(),
  successRate: z.number(),
});
export type BuilderTopToolsRow = z.infer<typeof BuilderTopToolsRowSchema>;

export const BuilderTopToolsResponseSchema = z.object({
  range: z.object({ from: z.string(), to: z.string() }),
  builderKind: BuilderKindSchema,
  rows: z.array(BuilderTopToolsRowSchema),
});
export type BuilderTopToolsResponse = z.infer<
  typeof BuilderTopToolsResponseSchema
>;

export const useBuilderTopTools = (opts: {
  builder: BuilderKind;
  range?: DateRange;
  limit?: number;
  failuresFirst?: boolean;
}) => {
  const range = opts.range ?? {};
  const sp = new URLSearchParams();
  sp.set('builder', opts.builder);
  if (opts.limit !== undefined) sp.set('limit', String(opts.limit));
  if (opts.failuresFirst !== undefined)
    sp.set('failuresFirst', String(opts.failuresFirst));
  if (range.from) sp.set('from', range.from);
  if (range.to) sp.set('to', range.to);

  return createSysadminQuery({
    key: [
      'sysadmin-builder-analytics-top-tools',
      opts.builder,
      range.from ?? 'default',
      range.to ?? 'default',
      opts.limit ?? 10,
      opts.failuresFirst ?? false,
    ],
    url: `/admin/platform/builder-analytics/top-tools?${sp.toString()}`,
    schema: BuilderTopToolsResponseSchema,
  })();
};

// ─── Per-tenant breakdown ────────────────────────────────────────────────

export const BuilderPerTenantRowSchema = z.object({
  organizationId: z.string().uuid().nullable(),
  organizationName: z.string().nullable(),
  turnCount: z.number().int(),
  firstTokenP95Ms: z.number().int().nullable(),
  totalP95Ms: z.number().int().nullable(),
  successRate: z.number(),
});
export type BuilderPerTenantRow = z.infer<typeof BuilderPerTenantRowSchema>;

export const BuilderPerTenantResponseSchema = z.object({
  range: z.object({ from: z.string(), to: z.string() }),
  rows: z.array(BuilderPerTenantRowSchema),
});
export type BuilderPerTenantResponse = z.infer<
  typeof BuilderPerTenantResponseSchema
>;

export const useBuilderPerTenant = (range: DateRange = {}) =>
  createSysadminQuery({
    key: [
      'sysadmin-builder-analytics-per-tenant',
      range.from ?? 'default',
      range.to ?? 'default',
    ],
    url: `/admin/platform/builder-analytics/per-tenant${buildQs(range)}`,
    schema: BuilderPerTenantResponseSchema,
  })();

// ─── Publish gate ────────────────────────────────────────────────────────

export const PublishGateRowSchema = z.object({
  entityKind: z.string(),
  approvalCount: z.number().int(),
  timeInPendingMsP50: z.number().int().nullable(),
  timeInPendingMsP95: z.number().int().nullable(),
});
export type PublishGateRow = z.infer<typeof PublishGateRowSchema>;

export const PublishGateResponseSchema = z.object({
  range: z.object({ from: z.string(), to: z.string() }),
  rows: z.array(PublishGateRowSchema),
});
export type PublishGateResponse = z.infer<typeof PublishGateResponseSchema>;

export const usePublishGateSummary = (range: DateRange = {}) =>
  createSysadminQuery({
    key: [
      'sysadmin-builder-analytics-publish-gate',
      range.from ?? 'default',
      range.to ?? 'default',
    ],
    url: `/admin/platform/builder-analytics/publish-gate${buildQs(range)}`,
    schema: PublishGateResponseSchema,
  })();

// ─── Display helpers ─────────────────────────────────────────────────────

/**
 * GREEN ≥ 80%, YELLOW 60-79%, RED < 60%. The denominator is
 * (discovery_turn_count + stub_draft_count). When the denominator is
 * zero (no T1 turns observed yet), returns 'unknown'.
 */
export type DiscoveryBadge = 'green' | 'yellow' | 'red' | 'unknown';

export function discoveryBadge(card: BuilderSummaryCard): DiscoveryBadge {
  const denom = card.discoveryTurnCount + card.stubDraftCount;
  if (denom === 0) return 'unknown';
  if (card.discoveryContractPassRate >= 0.8) return 'green';
  if (card.discoveryContractPassRate >= 0.6) return 'yellow';
  return 'red';
}

export function formatMs(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value < 1000) return `${value}ms`;
  return `${(value / 1000).toFixed(1)}s`;
}

export function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value))
    return '—';
  return `${(value * 100).toFixed(1)}%`;
}
